import React, { useState } from 'react'
import { sortBy } from 'lodash'
import Box from '@material-ui/core/Box'
import ContactlessOutlinedIcon from 'components/material-symbol/icons/Contactless'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import ReportProblemOutlinedIcon from 'components/material-symbol/icons/ReportProblem'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import ErrorOutlineOutlinedIcon from 'components/material-symbol/icons/ErrorOutline'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import MoreHorizOutlinedIcon from 'components/material-symbol/icons/MoreHoriz'
import LaunchOutlinedIcon from 'components/material-symbol/icons/Launch'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import AccountCard from 'pages/account/account-details/account-card'
import ManageStripeAccountConfirmationDialog from './manage-stripe-account-confirmation-dialog'
import EditPaymentAccountNameDialog from './edit-payment-account-name-dialog'
import { useIsMobile } from 'lib/hooks'
import {
  INCOMPLETE_STRIPE_SETUP_TIP,
  CHARGES_DISABLED_OR_ACCOUNT_DELETED_TIP,
  PAYOUTS_DISABLED_TIP,
} from 'utils/constants'
import { useStyles } from './styles'

const PaymentAccountActionMenu = ({
  paymentAccount,
  trigger,
  refetch,
  onManageStripeClick,
}) => {
  const classes = useStyles()
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null)
  const [isEditNameDialogOpen, setIsEditNameDialogOpen] = useState(false)

  const openMenu = e => setActionMenuAnchor(e.target)
  const closeMenu = () => setActionMenuAnchor(null)
  const { detailsSubmitted } = paymentAccount

  return (
    <>
      <trigger.type {...trigger.props} onClick={openMenu} />
      <Menu
        open={Boolean(actionMenuAnchor)}
        onClose={closeMenu}
        anchorEl={actionMenuAnchor}
        disableAutoFocus={true}
        disableAutoFocusItem={true}
        disableRestoreFocus={true}
        disableEnforceFocus={true}
      >
        <MenuItem
          onClick={() => {
            closeMenu()
            onManageStripeClick()
          }}
        >
          <ListItemIcon className={classes.menuItemIcon}>
            <LaunchOutlinedIcon fontSize="small" opticalSize={20} />
          </ListItemIcon>
          <ListItemText
            primary={detailsSubmitted ? 'Manage Stripe' : 'Continue Setup'}
          />
        </MenuItem>
        <MenuItem onClick={() => setIsEditNameDialogOpen(true)}>
          <ListItemIcon className={classes.menuItemIcon}>
            <EditOutlinedIcon fontSize="small" opticalSize={20} />
          </ListItemIcon>
          <ListItemText primary="Edit Name" />
        </MenuItem>
      </Menu>
      <EditPaymentAccountNameDialog
        paymentAccount={paymentAccount}
        isOpen={isEditNameDialogOpen}
        onClose={() => {
          closeMenu()
          setIsEditNameDialogOpen(false)
        }}
        onSuccess={() => {
          closeMenu()
          setIsEditNameDialogOpen(false)
          refetch()
        }}
      />
    </>
  )
}

const PaymentAccountRow = ({ paymentAccount, refetch }) => {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const [isManageStripeDialogOpen, setIsManageStripeDialogOpen] = useState(
    false,
  )

  const {
    name,
    detailsSubmitted,
    chargesEnabled,
    payoutsEnabled,
  } = paymentAccount
  const isFullySetUp = detailsSubmitted && chargesEnabled && payoutsEnabled

  const openManageStripeDialog = () => setIsManageStripeDialogOpen(true)

  const warningAction = (
    <TipWellActionButton type="button" onClick={openManageStripeDialog}>
      {detailsSubmitted ? 'Manage Stripe' : 'Continue Setup'}
    </TipWellActionButton>
  )

  const renderWarning = (testId, caption, icon, variant = 'neutral') => (
    <Box data-testid={testId} mb={1.5}>
      <TipWell
        variant={variant}
        icon={icon}
        caption={caption}
        action={warningAction}
      />
    </Box>
  )

  const getChipsForAccount = () => {
    const chips = []
    if (isFullySetUp) {
      chips.push(
        <Chip
          key="active"
          label="Active"
          size="small"
          className={`${classes.statusChip} ${classes.activeChip}`}
        />,
      )
      return chips
    } else if (!detailsSubmitted) {
      chips.push(
        <Chip
          key="incomplete"
          label="Stripe Setup Incomplete"
          size="small"
          className={`${classes.statusChip} ${classes.stripeSetupIncompleteChip}`}
          icon={<ErrorOutlineOutlinedIcon className={classes.errorIcon} />}
        />,
      )
      return chips
    }
    if (!chargesEnabled) {
      chips.push(
        <Chip
          key="unable"
          label="Unable to Accept Payments"
          size="small"
          className={`${classes.statusChip} ${classes.unableToAcceptPaymentsChip}`}
          icon={<ErrorOutlineOutlinedIcon className={classes.errorIcon} />}
        />,
      )
    }
    if (!payoutsEnabled) {
      chips.push(
        <Chip
          key="payouts"
          label="Payouts on Hold"
          size="small"
          className={`${classes.statusChip} ${classes.payoutsOnHoldChip}`}
          icon={<ErrorOutlineOutlinedIcon className={classes.errorIcon} />}
        />,
      )
    }
    return chips
  }

  const getWarningBlocks = () => {
    if (!detailsSubmitted) {
      return renderWarning(
        'details-not-submitted-warning',
        INCOMPLETE_STRIPE_SETUP_TIP,
        InfoOutlinedIcon,
      )
    }

    const hasChargesIssue = !chargesEnabled
    const hasPayoutsIssue = !payoutsEnabled

    if (hasChargesIssue && hasPayoutsIssue) {
      return (
        <Box data-testid="account-issues-warning" mb={1.5}>
          <TipWell
            variant="error"
            icon={ErrorOutlineOutlinedIcon}
            caption={
              <>
                {CHARGES_DISABLED_OR_ACCOUNT_DELETED_TIP}
                <br />
                {PAYOUTS_DISABLED_TIP}
              </>
            }
            action={warningAction}
          />
        </Box>
      )
    }

    if (hasChargesIssue) {
      return renderWarning(
        'charges-disabled-warning',
        CHARGES_DISABLED_OR_ACCOUNT_DELETED_TIP,
        ErrorOutlineOutlinedIcon,
        'error',
      )
    }

    if (hasPayoutsIssue) {
      return renderWarning(
        'payouts-disabled-warning',
        PAYOUTS_DISABLED_TIP,
        ReportProblemOutlinedIcon,
        'warning',
      )
    }

    return null
  }

  return (
    <>
      <Box className={classes.accountRow}>
        <Box className={classes.rowMain}>
          <Box className={classes.iconWrap}>
            <ContactlessOutlinedIcon
              data-testid="contactless-icon"
              className={classes.accountIcon}
              fontSize="small"
              opticalSize={20}
            />
          </Box>
          <Box minWidth={0}>
            <Typography className={classes.accountName}>{name}</Typography>
            {isMobile && (
              <Box className={classes.mobileChipRow}>
                {getChipsForAccount()}
              </Box>
            )}
          </Box>
        </Box>
        {!isMobile && (
          <Box className={classes.chipRow}>{getChipsForAccount()}</Box>
        )}
        <Box className={classes.rowActions}>
          <PaymentAccountActionMenu
            paymentAccount={paymentAccount}
            onManageStripeClick={openManageStripeDialog}
            trigger={
              <IconButton className={classes.menuButton} size="small">
                <MoreHorizOutlinedIcon color="inherit" aria-label="more" />
              </IconButton>
            }
            refetch={refetch}
          />
        </Box>
      </Box>
      {getWarningBlocks()}
      <ManageStripeAccountConfirmationDialog
        paymentAccount={paymentAccount}
        isOpen={isManageStripeDialogOpen}
        onClose={() => setIsManageStripeDialogOpen(false)}
      />
    </>
  )
}

const PaymentAccountsDisplay = ({ paymentAccounts, refetch }) => {
  const classes = useStyles()

  return (
    <AccountCard title="Payment Accounts" classes={classes}>
      <Box>
        {sortBy(paymentAccounts, ['name']).map((pa, idx) => (
          <Box key={pa.id}>
            {idx > 0 && <Divider className={classes.rowDivider} />}
            <PaymentAccountRow paymentAccount={pa} refetch={refetch} />
          </Box>
        ))}
      </Box>
    </AccountCard>
  )
}

export default PaymentAccountsDisplay
